import { z } from 'zod';
// Core i18next, NOT '@/i18n/config' — config pulls react-i18next into every schema module and breaks suites that mock it. Same singleton.
import i18n from 'i18next';

export type MessageParams = Record<string, string | number>;

// Thunk: $t() nesting does not expand inside interpolation VALUES, so translated params must resolve per-parse.
export type MessageParamsInput = MessageParams | (() => MessageParams);

const resolveParams = (params?: MessageParamsInput): MessageParams | undefined =>
  typeof params === 'function' ? params() : params;

export const translate = (key: string, params?: MessageParams): string => {
  // i18n.t is undefined before init(); return the key rather than throw.
  if (typeof i18n.t !== 'function') return key;
  const t = i18n.t.bind(i18n) as (k: string, o?: MessageParams) => unknown;
  const value = t(key, params);
  return typeof value === 'string' ? value : key;
};

// Resolved when Zod materializes an issue, so module-level schemas track the active language.
export const msg = (key: string, params?: MessageParamsInput) => ({
  error: () => translate(key, resolveParams(params)),
});

// ctx.addIssue() takes `message`, NOT `error` — `{ error }` compiles clean but silently yields "Invalid input".
export const issue = (key: string, path: PropertyKey[], params?: MessageParamsInput) => ({
  code: 'custom' as const,
  message: translate(key, resolveParams(params)),
  path,
});

/** Materialize localized issues before the active language can change. */
export const safeParseLocalized = <T extends z.ZodType>(schema: T, input: unknown) => {
  const result = schema.safeParse(input);
  if (!result.success) {
    // Zod defers error construction until the first read.
    return { success: false as const, error: result.error };
  }
  return result;
};
